// ============================================================================
// USER PROFILE PAGE
// View and manage user profile settings
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  Mail,
  Shield,
  Building,
  CheckCircle,
  XCircle,
  Pencil,
  Save,
  X,
  Lock,
  ArrowLeft,
  Clock,
  ShieldCheck,
  Eye,
  EyeOff,
  AlertTriangle
} from 'lucide-react';
import { api } from '../services/api';
import { updatePassword } from 'aws-amplify/auth';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: ''
  });

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleOpenPasswordModal = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    setPasswordError(null);

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordForm.newPassword === passwordForm.currentPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setChangingPassword(true);
      await updatePassword({
        oldPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setShowPasswordModal(false);
      setSuccess('Password changed successfully!');
    } catch (err: any) {
      const message = err.message || 'Failed to change password';
      if (message.includes('Incorrect') || message.includes('NotAuthorizedException')) {
        setPasswordError('Current password is incorrect');
      } else if (message.includes('policy') || message.includes('InvalidPasswordException')) {
        setPasswordError('New password does not meet requirements. Use at least 8 characters with uppercase, lowercase, numbers, and symbols.');
      } else {
        setPasswordError(message);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // Initialize form with user data when user changes
  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        displayName: user.displayName || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (user) {
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        displayName: user.displayName || '',
        email: user.email || ''
      });
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setUpdating(true);
      setError(null);
      setSuccess(null);

      const updates: any = {};
      if (profileForm.firstName !== user?.firstName) updates.firstName = profileForm.firstName;
      if (profileForm.lastName !== user?.lastName) updates.lastName = profileForm.lastName;
      if (profileForm.displayName !== user?.displayName) updates.displayName = profileForm.displayName;
      if (profileForm.email !== user?.email) updates.email = profileForm.email;

      if (Object.keys(updates).length === 0) {
        setSuccess('No changes to save');
        setIsEditing(false);
        return;
      }

      const response = await api.patch('/api/users/me', updates);

      setSuccess(response.data.message || 'Profile updated successfully!');
      setIsEditing(false);

      if (refreshUser) {
        await refreshUser();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  if (!user) {
    return (
      <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-[#f8fafc] text-[#052831]">
        <div className="flex justify-center items-center py-20 text-[#64748b]">
          <div className="text-base font-medium">Loading user profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-[#f8fafc] text-[#052831] font-display">
      {/* Main */}
      <main className="flex-1 max-w-[1280px] mx-auto w-full px-6 lg:px-20 py-10">
        {/* Back button */}
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2 text-[#64748b] hover:text-[#052831] font-medium text-sm mb-6 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </button>

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div className="space-y-1">
            <h2 className="text-4xl font-black text-[#052831] tracking-tight">User Profile</h2>
            <p className="text-[#64748b] font-medium">Manage your account settings</p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="flex items-center gap-3 px-4 py-3 mb-6 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
            <span className="text-sm font-medium text-green-700">{success}</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 px-4 py-3 mb-6 bg-red-50 border border-red-200 rounded-xl">
            <XCircle size={18} className="text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium text-red-700">{error}</span>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {/* Profile Information Card */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <User size={18} className="text-primary" />
                </div>
                <h3 className="font-bold text-[#052831] text-lg">Profile Information</h3>
              </div>
              {!isEditing && (
                <button
                  onClick={handleEditClick}
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-[#052831] font-bold px-4 py-2 rounded-xl transition-all text-sm"
                >
                  <Pencil size={14} />
                  Edit Profile
                </button>
              )}
            </div>

            <div className="px-6 py-6">
              {isEditing ? (
                /* Edit Mode */
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                        <User size={14} />
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 text-sm text-[#052831] outline-none transition-all"
                        placeholder="First name"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                        <User size={14} />
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 text-sm text-[#052831] outline-none transition-all"
                        placeholder="Last name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                        <User size={14} />
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.displayName}
                        onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 text-sm text-[#052831] outline-none transition-all"
                        placeholder="Display name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                        <Mail size={14} />
                        Email
                      </label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 text-sm text-[#052831] outline-none transition-all"
                        placeholder="Email address"
                      />
                      <p className="text-xs text-slate-400 mt-1.5">
                        Email changes will be verified through Cognito
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                    <button
                      onClick={handleCancelEdit}
                      disabled={updating}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#e2e8f0] text-[#64748b] font-medium rounded-xl hover:bg-slate-50 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X size={14} />
                      Cancel
                    </button>
                    <button
                      onClick={handleProfileUpdate}
                      disabled={updating}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-[#052831] font-bold rounded-xl transition-all text-sm shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save size={14} />
                      {updating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <User size={14} className="text-slate-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Display Name
                      </span>
                    </div>
                    <div className="text-sm font-medium text-[#052831]">
                      {user.displayName || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Mail size={14} className="text-slate-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Email
                      </span>
                    </div>
                    <div className="text-sm font-medium text-[#052831]">
                      {user.email}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Shield size={14} className="text-slate-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Role
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#052831] capitalize">
                        {user.role}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">
                        {user.role}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Building size={14} className="text-slate-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Organization
                      </span>
                    </div>
                    <div className="text-sm font-medium text-[#052831]">
                      {user.organizationId || 'Not set'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Account Security Card */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Lock size={18} className="text-primary" />
              </div>
              <h3 className="font-bold text-[#052831] text-lg">Account Security</h3>
            </div>

            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <ShieldCheck size={14} className="text-slate-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Account Status
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                      Active
                    </span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock size={14} className="text-slate-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Authentication
                    </span>
                  </div>
                  <div className="text-sm font-medium text-[#052831]">
                    AWS Cognito SSO
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100">
                <button
                  onClick={handleOpenPasswordModal}
                  className="flex items-center gap-2 bg-primary/10 text-primary font-bold px-5 py-2.5 rounded-xl hover:bg-primary/20 transition-all text-sm"
                >
                  <Lock size={14} />
                  Change Password
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !changingPassword && setShowPasswordModal(false)}
          />
          <div className="relative bg-white rounded-2xl border border-[#e2e8f0] shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Lock size={18} className="text-primary" />
                </div>
                <h3 className="font-bold text-[#052831] text-lg">Change Password</h3>
              </div>
              <button
                onClick={() => !changingPassword && setShowPasswordModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#052831] hover:bg-slate-100 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 flex flex-col gap-4">
              {passwordError && (
                <div className="flex items-start gap-3 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-700">{passwordError}</span>
                </div>
              )}

              {/* Current Password */}
              <div>
                <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <Lock size={14} />
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 pr-10 text-sm text-[#052831] outline-none transition-all"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <Lock size={14} />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 pr-10 text-sm text-[#052831] outline-none transition-all"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">
                  At least 8 characters with uppercase, lowercase, numbers, and symbols
                </p>
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <Lock size={14} />
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
                  className="w-full rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm h-11 px-3 text-sm text-[#052831] outline-none transition-all"
                  placeholder="Re-enter new password"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
              <button
                onClick={() => setShowPasswordModal(false)}
                disabled={changingPassword}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#e2e8f0] text-[#64748b] font-medium rounded-xl hover:bg-slate-50 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-[#052831] font-bold rounded-xl transition-all text-sm shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Lock size={14} />
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;
